// Play controller (presentation-2 contract section 6.3). Owns the draft (model/choices.ts, unchanged),
// the active target set, popovers and the action bar. Bricks ask it for each anchor's visual state
// and forward activations; the host still judges every submitted command.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Session, SessionState } from '../session/Session';
import type { TableModel } from '../model/table';
import { ownsDecision } from '../model/table';
import {
  answer, assemble, back, canBeFree, canGoBack, choiceEligible, currentStep, freeDefault, isComplete, sameChoiceDescriptor, startDraft, stepsAfter, validateStep,
  type ChoiceView, type Draft, type StepValue,
} from '../model/choices';
import type { Scalar } from '../protocol/ui';
import { objectAnchor, resolveTargets, type TargetEntry, type TargetMap } from './targets';
import { choiceFamily } from './family';
import type { TargetRule } from '../presentation/types';
import { errorText } from '../protocol/wire';
import { retainsPeerDecisionDraft } from './draftRevision';
import { useDraftContext } from './draftContext';
import { AutoAnswerSettingContext } from './autoAnswerSetting';
import { autoAnswerDelay, autoAnswerOf, readAutoAnswer, writeAutoAnswer, type AutoAnswerRule } from './autoAnswer';

export type StepUi = 'objects' | 'popover' | 'complete';

export interface ActiveDraft {
  choice: ChoiceView;
  draft: Draft;
  /** The anchor the draft started from; popovers attach here. */
  anchor: string;
  /** Selection in progress for a multi or ordered object pick. */
  multi: Scalar[];
  /** Started from a target that pre-answered its first pick (eligible for quick submit). */
  quick: boolean;
}

export interface AnchorState {
  selectable: boolean;
  /** Related to the draft in progress (for example the hand cards a payment uses): drawn lifted. */
  related: boolean;
  option: boolean;
  selected: boolean;
  dimmed: boolean;
  pending: boolean;
  rejected: boolean;
  /** Position in an ordered pick, 1-based. */
  order: number | null;
  /** A selectable target whose move can be made without paying (interactions `preferFree`). */
  free?: boolean;
  /** Lit because a hovered piece names it (a brick's `linkField`), e.g. the Construction a Critter moves into. */
  linked?: boolean;
  tip: string | null;
  button: string | null;
  /** Drawn as hovered without a pointer (the brick gallery shows every state at once). */
  hovered?: boolean;
}

const IDLE: AnchorState = { related: false, selectable: false, option: false, selected: false, dimmed: false, pending: false, rejected: false, order: null, tip: null, button: null };

export interface PlayApi {
  /** Displayed revision, used to invalidate an editor's explicit review after a receipt. */
  revision?: number;
  /** Buffered/replay positions cannot own local editors. */
  readOnly?: boolean;
  /** A submitted command freezes target activation and inspection until resolved. */
  pending?: boolean;
  /** Seat-filtered descriptors available to reusable collection editors. */
  choices?: ChoiceView[];
  isMyTurn: boolean;
  targets: TargetMap;
  /** Anchors the current step lights: option objects during an object pick, else every target while idle. */
  lit: string[];
  /** A key that changes whenever the step the viewer faces changes (views auto-focus once per step). */
  stepKey: string;
  active: ActiveDraft | null;
  /** Anchor whose choice popover (several choices) is open. */
  chooser: string | null;
  stepUi: StepUi | null;
  stateOf: (anchor: string) => AnchorState;
  activate: (anchor: string) => void;
  chooseEntry: (anchor: string, entry: TargetEntry) => void;
  /** Action kinds that open as one form at their anchor (`interactions.forms`). */
  forms: Set<string>;
  /** Submits a complete draft built outside the step flow (a choice-family form at this anchor). */
  submitDraft: (anchor: string, choice: ChoiceView, draft: Draft) => void;
  /** Advisory preview only; complete drafts still use the ordinary submission gate. */
  previewDraft?: (draft: Draft) => Promise<{ ok: boolean; reason?: string }>;
  answerStep: (value: StepValue) => void;
  /** Confirms the current step (multi pick or allocation in progress) and submits when complete. */
  confirm: (pendingValue?: StepValue) => void;
  goBack: () => void;
  cancel: () => void;
  closeChooser: () => void;
  inspect: (objectId: string | null) => void;
  inspected: string | null;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  /** Board nodes lit by a hovered objective (cross-brick link). */
  highlight: Set<string> | null;
  setHighlight: (nodes: Set<string> | null) => void;
  /** Object IDs a hovered piece links to (cross-brick link by `linkField`). */
  setLinked: (ids: Set<string> | null) => void;
  lastError: string | null;
  /** The viewer's switch for an `autoAnswer` rule; `waiting` while the client is about to send the only choice. */
  auto?: { label: string; enabled: boolean; waiting: boolean; setEnabled: (on: boolean) => void } | null;
}

const PlayContext = createContext<PlayApi | null>(null);
export const usePlay = () => useContext(PlayContext);
/** For pages that drive bricks without a game (the brick gallery). */
export const PlayProvider = PlayContext.Provider;
export const IDLE_STATE = IDLE;

/** Option values of the current step, keyed by object anchor, when it is an object pick. */
export function optionAnchors(draft: Draft | null): Map<string, Scalar> | null {
  const step = draft ? currentStep(draft) : null;
  if (!step || step.type !== 'pick' || step.expand || step.options.length === 0 || !step.options.every(o => !!o.objectId)) return null;
  return new Map(step.options.map(o => [objectAnchor(o.objectId!), o.value]));
}

function stepUiOf(draft: Draft, listPicks = false): StepUi {
  if (isComplete(draft)) return 'complete';
  return !listPicks && optionAnchors(draft) ? 'objects' : 'popover';
}

interface Props {
  session: Session;
  state: SessionState;
  table: TableModel;
  choices: ChoiceView[];
  rules: TargetRule[];
  quickSubmit: boolean;
  /** Mark targets that can be played without paying, and default a pick to its only free option. */
  preferFree?: boolean;
  /** Object picks are answered from the step list (the objects are not drawn): no table clicking. */
  listPicks?: boolean;
  /** Action kinds that open as one form (`interactions.forms`). */
  forms?: string[];
  /** interactions.json `autoAnswer`: a per-viewer convenience that sends the only stepless choice of a declared kind. */
  autoAnswer?: AutoAnswerRule[];
  /** Displayed revision: unchanged independent decisions retain local drafts across peer commits. */
  revision: number;
  /** The shown position is behind the session's latest one (animations still catching up): no input. */
  stale?: boolean;
  children: React.ReactNode;
}

export function PlayController(props: Props) {
  const context = useDraftContext({ session: props.session, gameId: props.state.gameId, matchId: props.state.matchId, seat: props.state.seat });
  return <CurrentPlayController key={context} {...props} />;
}

function CurrentPlayController({ session, state, table, choices, rules, quickSubmit, preferFree = false, listPicks = false, forms, autoAnswer, revision, stale = false, children }: Props) {
  const pickAnchors = useCallback((draft: Draft | null) => (listPicks ? null : optionAnchors(draft)), [listPicks]);
  const formKinds = useMemo(() => new Set(forms ?? []), [forms]);
  const eligibleChoices = useMemo(() => choices.filter(choice => choiceEligible(table, choice)), [choices, table]);
  const live = !table.finished && !stale && !state.paused && state.connection === 'open';
  const isMyTurn = live && (ownsDecision(table) || eligibleChoices.length > 0);
  const targets = useMemo(() => resolveTargets(live ? eligibleChoices : [], table, rules), [eligibleChoices, table, rules, live]);
  const [activeDraft, setActive] = useState<ActiveDraft | null>(null);
  const [chooser, setChooser] = useState<string | null>(null);
  const [inspected, setInspected] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [highlight, setHighlight] = useState<Set<string> | null>(null);
  const [linked, setLinked] = useState<Set<string> | null>(null);
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const [rejected, setRejected] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const submittedRef = useRef<string | null>(null);
  const previousPosition = useRef(table);
  const previousDecision = useRef({ revision, decisionId: state.decisionId });
  // Hide invalid answers during render, before reset effects run. Unchanged
  // auxiliary/peer decisions retain their existing narrow revision exception.
  const active = activeDraft && !table.finished && !state.paused && choiceEligible(table, activeDraft.choice) && sameChoiceDescriptor(choices.find(c => c.id === activeDraft.choice.id), activeDraft.choice) &&
    (previousDecision.current.revision === revision && (stale || previousDecision.current.decisionId === state.decisionId) || activeDraft.choice.scope === 'auxiliary' || retainsPeerDecisionDraft(previousPosition.current, table, activeDraft.choice, choices)) ? activeDraft : null;
  const [interrupted, setInterrupted] = useState(false);

  useEffect(() => {
    // Session already revokes approval against the live receipt. The displayed
    // motion frame must catch up before comparing its decision domain.
    if (stale) { session.invalidateChoiceCheck(); return; }
    session.invalidateChoiceCheck();
    // A reconnect can replace the observation without changing the decision.
    // Catching up that frame is not a revision boundary for local answers.
    if (previousDecision.current.revision === revision && previousDecision.current.decisionId === state.decisionId) {
      previousPosition.current = table;
      return;
    }
    const previous = previousPosition.current;
    previousPosition.current = table;
    previousDecision.current = { revision, decisionId: state.decisionId };
    // Local answers may survive a peer lock, but every approval is bound to the
    // exact latest revision and must be obtained again before submission.
    setActive(a => a && (a.choice.scope === 'auxiliary' && choiceEligible(table, a.choice) || retainsPeerDecisionDraft(previous, table, a.choice, choices)) ? a : null);
    setChooser(null); setPendingAnchor(null); setLastError(null); setInspected(null);
  }, [revision, state.decisionId, stale, session]);
  useEffect(() => {
    if (!live) session.invalidateChoiceCheck();
    // A displayed motion frame suspends input; it does not discard a draft
    // before the authoritative next position can compare its decision domain.
    if (table.finished || state.paused || !stale && activeDraft && (!choiceEligible(table, activeDraft.choice) || !sameChoiceDescriptor(choices.find(c => c.id === activeDraft.choice.id), activeDraft.choice))) { setActive(null); setChooser(null); }
    if (state.connection !== 'open' && active) setInterrupted(true);
  }, [live, table, active, activeDraft, session, state.connection, state.paused, choices, stale]);
  const submittedDraft = useRef<ActiveDraft | null>(null);
  const [autoOn, setAutoOn] = useState(true);
  const autoCandidate = useMemo(() => autoAnswerOf(eligibleChoices, autoAnswer), [eligibleChoices, autoAnswer]);
  const autoKind = autoCandidate?.rule.kind ?? autoAnswer?.[0]?.kind ?? null;
  useEffect(() => { if (autoKind) setAutoOn(readAutoAnswer(state.gameId ?? '', autoKind)); }, [state.gameId, autoKind]);
  const setAutoEnabled = useCallback((on: boolean) => { setAutoOn(on); if (autoKind) writeAutoAnswer(state.gameId ?? '', autoKind, on); }, [state.gameId, autoKind]);
  useEffect(() => { if (inspected && !table.objects.has(inspected)) setInspected(null); }, [table, inspected]);

  // A rejection shakes the anchor once and shows the notice in the action bar.
  useEffect(() => {
    if (!state.pending && submittedRef.current && state.notice) {
      const anchor = submittedRef.current;
      setRejected(anchor); setPendingAnchor(null); setLastError(state.notice.text || errorText(state.notice.code));
      const sent = submittedDraft.current;
      if (state.notice.code === 'stale_revision' && sent && isMyTurn && sameChoiceDescriptor(choices.find(c => c.id === sent.choice.id), sent.choice)) {
        setActive({ ...sent, quick: false });
        setLastError('The table updated. Your unchanged selections are retained; review and confirm again.');
      }
      const timer = setTimeout(() => setRejected(r => (r === anchor ? null : r)), 650);
      submittedRef.current = null;
      submittedDraft.current = null;
      return () => clearTimeout(timer);
    }
    if (!state.pending) { submittedRef.current = null; submittedDraft.current = null; }
    return undefined;
  }, [state.pending, state.notice]);

  const submit = useCallback(async (a: ActiveDraft, draft: Draft) => {
    if (!live || !choiceEligible(table, a.choice)) return;
    setInterrupted(false);
    setActive({ ...a, draft });
    if (await session.submitDraft(draft, a.choice.label)) {
      submittedDraft.current = { ...a, draft };
      submittedRef.current = a.anchor; setPendingAnchor(a.anchor); setActive(null); setLastError(null);
    }
  }, [session, live, table]);

  // Auto-answer: after one uniform delay the client sends the only choice (a pass) exactly as pressing its button would.
  const autoWaiting = !!autoCandidate && autoOn && live && !state.pending && !activeDraft;
  const submitRef = useRef(submit); submitRef.current = submit;
  useEffect(() => {
    if (!autoWaiting || !autoCandidate) return undefined;
    const { choice, rule } = autoCandidate;
    const timer = setTimeout(() => submitRef.current({ choice, draft: startDraft(choice), anchor: `auto:${choice.id}`, multi: [], quick: true }, startDraft(choice)), autoAnswerDelay(rule));
    return () => clearTimeout(timer);
    // One timer per decision: a new revision or decision restarts it, local re-renders do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoWaiting, autoCandidate?.choice.id, revision, state.decisionId]);

  /** Moves a draft forward: quick-submits a pre-answered stepless draft, or shows its next step. */
  const advance = useCallback((a: ActiveDraft) => {
    if (isComplete(a.draft) && a.quick && quickSubmit && !a.choice.confirm) { submit(a, a.draft); return; }
    // The only free way through a pick is taken for the player; Back returns to the pick.
    const free = preferFree ? freeDefault(a.draft) : null;
    if (free !== null) { setActive({ ...a, draft: answer(a.draft, free), quick: false }); return; }
    setActive(a);
  }, [quickSubmit, preferFree, submit]);

  const begin = useCallback((anchor: string, entry: TargetEntry) => {
    const choice = eligibleChoices.find(c => c.id === entry.choiceId);
    if (!choice || !live || state.pending) return;
    session.invalidateChoiceCheck();
    setInspected(null);
    setChooser(null); setLastError(null); setInterrupted(false);
    let draft = startDraft(choice);
    let multi: Scalar[] = [];
    let quick = isComplete(draft);
    const step = currentStep(draft);
    // A choice may name a board cell as a subject and as a first-step option.
    // In that case the cell click has already supplied the option's answer.
    const subjectAnswer = entry.answer === undefined && anchor.startsWith('obj:') && step?.type === 'pick'
      ? step.options.find(o => o.objectId === anchor.slice(4))?.value : undefined;
    const selectedAnswer = entry.answer ?? subjectAnswer;
    if (selectedAnswer !== undefined && step?.type === 'pick') {
      if ((step.max ?? 1) === 1 && !step.ordered) {
        if (!validateStep(step, selectedAnswer)) { draft = answer(draft, selectedAnswer); quick = true; }
      } else multi = [selectedAnswer];
    }
    advance({ choice, draft, anchor, multi, quick });
  }, [eligibleChoices, live, state.pending, advance, session]);

  const activate = useCallback((anchor: string) => {
    if (stale) return;
    if (state.pending) return;
    if (!isMyTurn) {
      if (anchor.startsWith('obj:')) setInspected(anchor.slice(4));
      return;
    }
    if (active) {
      const options = pickAnchors(active.draft);
      const value = options?.get(anchor);
      if (value === undefined) return;
      const step = currentStep(active.draft)!;
      if (step.type !== 'pick') return;
      if ((step.max ?? 1) === 1 && !step.ordered) { advance({ ...active, draft: answer(active.draft, value), quick: true }); return; }
      const has = active.multi.includes(value);
      const multi = has ? active.multi.filter(v => v !== value) : active.multi.length < (step.max ?? 1) ? [...active.multi, value] : active.multi;
      setActive({ ...active, multi });
      return;
    }
    const entries = targets.anchors.get(anchor);
    if (!entries?.length) { if (anchor.startsWith('obj:')) setInspected(anchor.slice(4)); return; }
    const only = entries.length === 1 ? choices.find(c => c.id === entries[0]!.choiceId) : undefined;
    if (entries.length === 1 && !(only && entries[0]!.answer === undefined && formKinds.has(only.action.kind) && choiceFamily([only]))) begin(anchor, entries[0]!);
    else setChooser(anchor);
  }, [isMyTurn, state.pending, active, targets, begin, advance, choices, formKinds, stale]);

  const answerStep = useCallback((value: StepValue) => {
    if (!active || !live || !choiceEligible(table, active.choice)) return;
    session.invalidateChoiceCheck(); setInterrupted(false);
    try { advance({ ...active, draft: answer(active.draft, value), multi: [], quick: false }); setLastError(null); }
    catch (e) { setLastError((e as Error).message); }
  }, [active, advance, session, live, table]);

  const confirm = useCallback((pendingValue?: StepValue) => {
    if (!active || !live || !choiceEligible(table, active.choice) || state.queryCheck?.status === 'pending') return;
    let draft = active.draft;
    const step = currentStep(draft);
    try {
      const objectMultiPick = step?.type === 'pick' && !!pickAnchors(draft) && ((step.max ?? 1) !== 1 || !!step.ordered);
      if (step?.type === 'pick' && objectMultiPick) {
        const value = step.ordered ? active.multi : step.options.map(o => o.value).filter(v => active.multi.includes(v));
        draft = answer(draft, value);
      } else if (step && pendingValue !== undefined) {
        draft = answer(draft, pendingValue);
      } else if (step?.type === 'pick' && (step.min ?? 1) === 0 && !!pickAnchors(draft)) {
        // Confirming a single object pick that may stay empty (Rest without destroying a card).
        draft = answer(draft, []);
      }
    } catch (e) { setLastError((e as Error).message); return; }
    if (isComplete(draft)) submit(active, draft);
    else setActive({ ...active, draft, multi: [], quick: false });
  }, [active, submit, state.queryCheck, live, table]);

  const goBack = useCallback(() => {
    if (!active) return;
    session.invalidateChoiceCheck(); setInterrupted(false);
    if (active.multi.length) { setActive({ ...active, multi: [] }); return; }
    if (canGoBack(active.draft)) setActive({ ...active, draft: back(active.draft) });
    else setActive(null);
  }, [active, session]);

  const cancel = useCallback(() => { session.invalidateChoiceCheck(); setActive(null); setChooser(null); setLastError(null); setInterrupted(false); }, [session]);
  const submitDraft = useCallback((anchor: string, choice: ChoiceView, draft: Draft) => {
    if (!live || !choiceEligible(table, choice)) return;
    session.invalidateChoiceCheck();
    setChooser(null); submit({ choice, draft, anchor, multi: [], quick: false }, draft);
  }, [submit, live, table]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Escape') { if (drawerOpen) setDrawerOpen(false); else if (inspected) setInspected(null); else cancel(); return; }
      if ((e.target as HTMLElement)?.closest('input, textarea, select')) return;
      if (e.key.toLowerCase() === 'm') { e.preventDefault(); setDrawerOpen(o => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cancel, drawerOpen, inspected]);

  const options = active ? pickAnchors(active.draft) : null;
  // Objects the draft is about: the viewer's own pieces whose catalog key ends with a value the
  // draft names (a fixed data value, an answer, or an allocation key), e.g. train:red for color red.
  const relatedAnchors = useMemo(() => {
    const out = new Set<string>();
    if (!active || !table.selfSeat) return out;
    const words = new Set<string>();
    for (const v of Object.values(active.choice.action.data)) if (typeof v === 'string') words.add(v);
    for (const a of active.draft.answers) if (typeof a.value === 'string') words.add(a.value);
    const step = currentStep(active.draft);
    if (step?.type === 'allocate') for (const k of step.keys) words.add(k.key);
    for (const o of table.objects.values()) {
      const tail = o.catalogKey?.slice(o.catalogKey.lastIndexOf(':') + 1);
      if (o.owner === table.selfSeat && tail && words.has(tail)) out.add(objectAnchor(o.id));
    }
    return out;
  }, [active, table]);
  // Targets whose move can be made without paying, for the Free badge.
  const freeAnchors = useMemo(() => {
    const out = new Set<string>();
    if (!preferFree) return out;
    for (const [anchor, entries] of targets.anchors) for (const e of entries) {
      const choice = choices.find(c => c.id === e.choiceId);
      if (choice && canBeFree(stepsAfter(choice, e.answer))) { out.add(anchor); break; }
    }
    return out;
  }, [preferFree, targets, choices]);
  const picking = !!active && !isComplete(active.draft);
  const stateOf = useCallback((anchor: string): AnchorState => {
    if (pendingAnchor === anchor && state.pending) return { ...IDLE, pending: true, selected: true };
    if (state.pending) return IDLE;
    if (rejected === anchor) return { ...IDLE, rejected: true };
    if (active) {
      const value = options?.get(anchor);
      if (value !== undefined) {
        const index = active.multi.indexOf(value);
        const step = currentStep(active.draft);
        const ordered = step?.type === 'pick' && !!step.ordered;
        return { ...IDLE, option: true, selected: index >= 0, order: ordered && index >= 0 ? index + 1 : null, tip: optionLabel(active.draft, value) };
      }
      if (anchor === active.anchor) return { ...IDLE, selected: true, tip: active.choice.label };
      if (relatedAnchors.has(anchor)) return { ...IDLE, related: true };
      // An object pick dims everything else; other steps dim only the targets that compete with it.
      // Containers (a mat, a zone) never dim: the options inside them would dim with them.
      if (options) return /^(mat|zone):/.test(anchor) ? IDLE : { ...IDLE, dimmed: true };
      return picking && targets.anchors.has(anchor) ? { ...IDLE, dimmed: true } : IDLE;
    }
    const entries = targets.anchors.get(anchor);
    const isLinked = !!linked?.has(anchor.slice(4)) && anchor.startsWith('obj:');
    if (!entries?.length) return isLinked ? { ...IDLE, linked: true } : IDLE;
    const button = entries.find(e => e.button)?.button ?? null;
    return { ...IDLE, selectable: true, button, linked: isLinked, free: freeAnchors.has(anchor), tip: entries.length === 1 ? entries[0]!.label : `${entries.length} moves` };
  }, [active, options, picking, targets, pendingAnchor, rejected, state.pending, relatedAnchors, linked, freeAnchors]);

  const lit = useMemo(() => (active ? (options ? [...options.keys()] : []) : [...targets.anchors.keys()]), [active, options, targets]);
  const stepKey = `${active ? active.choice.id : revision}|${isMyTurn}|${active?.draft.answers.length ?? -1}`;
  const api: PlayApi = {
    revision,
    readOnly: stale || !!state.paused,
    pending: !!state.pending,
    choices,
    isMyTurn, targets, lit, stepKey, active, chooser, stepUi: active ? stepUiOf(active.draft, listPicks) : null, stateOf, activate,
    forms: formKinds, chooseEntry: begin, submitDraft, answerStep, confirm, goBack, cancel, closeChooser: () => setChooser(null),
    previewDraft: async draft => {
      if (!live || state.pending || !choiceEligible(table, draft.choice)) return { ok: false, reason: 'This choice is no longer available.' };
      const origin = state.revision;
      const reply = await session.query('check', { action: assemble(draft) });
      if (reply.revision !== origin) return { ok: false, reason: 'The table changed. Check again.' };
      const result = reply.result as { ok?: boolean; reason?: string };
      return { ok: result?.ok === true, reason: result?.reason };
    },
    inspect: id => { if (!state.pending) setInspected(id); }, inspected, drawerOpen, setDrawerOpen, highlight, setHighlight, setLinked,
    auto: autoCandidate || autoAnswer?.length ? { label: (autoCandidate?.rule ?? autoAnswer?.[0])?.label ?? 'Answer automatically', enabled: autoOn, waiting: autoWaiting, setEnabled: setAutoEnabled } : null,
    lastError: lastError ?? (active && interrupted ? state.connection === 'open' ? 'Connection restored. Your selections are retained; review and confirm again.' : 'Connection interrupted. Your draft is retained; retry when connected.' : null),
  };
  return <PlayContext.Provider value={api}><AutoAnswerSettingContext.Provider value={api.auto ?? null}>{children}</AutoAnswerSettingContext.Provider></PlayContext.Provider>;
}

function optionLabel(draft: Draft, value: Scalar): string | null {
  const step = currentStep(draft);
  return step?.type === 'pick' ? step.options.find(o => o.value === value)?.label ?? null : null;
}

/** Props every target element spreads: anchor attribute, classes, accessible name and handlers. */
export function targetProps(play: PlayApi | null, anchor: string, label: string, base = ''): {
  className: string; 'data-anchor': string; 'aria-label': string; title?: string; onClick: (e: React.MouseEvent) => void;
  onKeyDown: (e: React.KeyboardEvent) => void; tabIndex: number; role: string; 'aria-pressed'?: boolean; 'aria-disabled'?: boolean;
} {
  const s = play?.stateOf(anchor) ?? IDLE;
  const classes = [base, s.related && 'is-related', s.selectable && 'is-selectable', s.option && 'is-option', s.selected && 'is-selected', s.dimmed && 'is-dimmed', s.pending && 'is-pending', s.rejected && 'is-rejected', s.hovered && 'is-hovered', s.free && 'is-free', s.linked && 'is-linked'].filter(Boolean).join(' ');
  const suffix = s.option ? (s.selected ? '. Selected.' : '. Choose this.') : s.selectable ? `. ${s.tip ?? 'Has moves'}.${s.free ? ' Can be played free.' : ''}` : '';
  return {
    className: classes, 'data-anchor': anchor, 'aria-label': label + suffix + (s.pending ? '. Sending this selection.' : ''), ...(s.tip ? { title: s.tip } : {}),
    onClick: e => {
      if (play?.pending) { e.preventDefault(); e.stopPropagation(); return; }
      if (anchor.startsWith('obj:') && e.altKey) { e.preventDefault(); e.stopPropagation(); return; }
      // An inert piece inside a selectable target (a hand card on a mat that ends the season) lets the click through.
      const inert = !s.selectable && !s.option && !s.selected;
      if (inert && (e.currentTarget as HTMLElement).parentElement?.closest('.is-selectable')) return;
      e.stopPropagation(); play?.activate(anchor);
    },
    onKeyDown: e => { if (play?.pending) { e.preventDefault(); e.stopPropagation(); return; } if (anchor.startsWith('obj:') && e.altKey) { e.preventDefault(); e.stopPropagation(); return; } if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); play?.activate(anchor); } },
    tabIndex: play?.pending ? -1 : 0, role: 'button', ...(play?.pending ? { 'aria-disabled': true } : {}), ...(s.option ? { 'aria-pressed': s.selected } : {}),
  };
}
